import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { PERSONAL_SALE_MOBILE_SPEC, PERSONAL_SALE_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the "personal sale" welcome-offer arm — `symphonyPersonalSale`.
 *
 * Variant (`true`): the account is IN the sale's ramp. Control (`false`): it is not. This is only the
 * FLAG half — whether the banner/badge actually SHOW is `use-personal-sale.ts`, which also requires the
 * Free plan and the 24h window.
 *
 * It FAILS CLOSED to CONTROL — a gate that has not answered (or errored) shows nothing rather than
 * flashing an unramped promo at everyone, exactly like the sibling gate (`home2-flag`).
 *
 * A dev-tools **Feature toggles** override wins over the conducted value (`toggleOverride` is consulted
 * first) and is inert without a dev-tools grant, so a real user always gets the conducted value — while
 * the feature is being built, turn it on from Dev Tools ▸ Feature toggles (or use the personal-sale
 * preview, which additionally bypasses the plan + window).
 *
 * There are TWO arms: this web one, and `symphonyPersonalSaleMobile` — the SAME spec the phones conduct.
 * Web reads the mobile arm (`usePersonalSaleMobileEnabled`) ONLY for a picker open that arrived from a
 * native app, so the discount a native user sees on this web `/packages` follows the FT their app's own
 * banner was gated on. `use-personal-sale.ts` picks between the two by `isNativeArrival`.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function personalSaleEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(PERSONAL_SALE_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/personal-sale`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cached;
}

/**
 * Resolve the WEB arm for rendering. Starts false — control — so an account not in the variant never
 * sees the banner/badge flash in and out.
 *
 * `enabled` gates the read the same way the mobile arm is gated on `native`: conducting an experiment
 * IS an exposure, so a picker open that arrived from a native app must NOT hit this route (it would log
 * the native user against the web arm and pollute it). The gate passes `!native`, so a native session
 * never conducts the web arm and a web session never conducts the mobile one. Defaults `true` for any
 * caller that just wants the web arm unconditionally.
 */
export function usePersonalSaleEnabled(enabled = true): boolean {
  const [flag, setFlag] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void personalSaleEnabled().then((on) => {
      if (live) setFlag(on);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return enabled && flag;
}

let cachedMobile: Promise<boolean> | null = null;

/**
 * Client read of the NATIVE arm — `symphonyPersonalSaleMobile`. Same shape as `personalSaleEnabled`
 * (override-first, fail-closed, own cache), against `/api/flags/personal-sale-mobile`.
 */
export function personalSaleMobileEnabled(): Promise<boolean> {
  const forced = toggleOverride(PERSONAL_SALE_MOBILE_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cachedMobile) return cachedMobile;
  cachedMobile = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/personal-sale-mobile`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cachedMobile;
}

/**
 * Resolve the NATIVE arm, but ONLY when `enabled` (the open arrived from a native app). A plain web
 * session passes `false` and never fetches this route — no app-wide overhead for the common case — and
 * the hook can never return `true` unless the arrival was native.
 */
export function usePersonalSaleMobileEnabled(enabled: boolean): boolean {
  const [flag, setFlag] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void personalSaleMobileEnabled().then((on) => {
      if (live) setFlag(on);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return enabled && flag;
}
